/**
 * Integração com o Lacuna Web PKI para assinatura com Certificado Digital
 * ICP-Brasil (A1/A3). O pacote toca em `window` e depende da extensão de
 * browser do parceiro, por isso é carregado dinamicamente (client-side) sob
 * demanda — mantém a lib fora do bundle principal e só inicializa quando o
 * signatário realmente entra no fluxo de assinatura digital.
 *
 * A chave privada nunca sai da máquina do signatário: o browser assina
 * localmente contra o Rest PKI com o token devolvido pelo nosso `start`, e a
 * nossa API só recebe a assinatura resultante no `complete`.
 */
import { Api } from "../api/Assinafy"

let pkiInstance = null
let pkiReadyPromise = null

// Página oficial de instalação do plugin Web PKI (instalador nativo + extensão).
export const WEBPKI_INSTALL_URL = "https://get.webpkiplugin.com/Setup"

const onlyDigits = (value) => String(value || "").replace(/\D/g, "")

/**
 * Erro do Web PKI com o motivo real (PIN errado, token desconectado, etc.).
 * `code` e `origin` vêm da própria lib e ajudam a agrupar no Sentry.
 */
export class WebPkiError extends Error {
  constructor({ userMessage, message, code, origin, error } = {}) {
    super(userMessage || message || "Falha na extensão de assinatura digital.")
    this.name = "WebPkiError"
    this.code = code
    this.origin = origin
    this.details = error
  }
}

/**
 * Converte a "promise" custom do Web PKI em Promise nativa. Usa `.fail` e não
 * `.error`: o `.error` legado repassa só a mensagem como string e perde
 * `code`/`origin`.
 */
export const toPromise = (call) =>
  new Promise((resolve, reject) => call.success(resolve).fail((ex) => reject(new WebPkiError(ex))))

/**
 * Carrega o Web PKI e resolve com a instância quando a extensão está pronta.
 * Rejeita com `{ code: "not_installed" | "error", message }` quando a extensão
 * não está instalada/atualizada ou quando há falha de inicialização.
 */
export const loadWebPki = () => {
  if (pkiReadyPromise) {
    return pkiReadyPromise
  }

  pkiReadyPromise = import("web-pki")
    .then(({ default: LacunaWebPKI }) => {
      // Sem licença no localhost; produção exige uma licença de origem Lacuna.
      const license = process.env.REACT_APP_WEBPKI_LICENSE || undefined
      const pki = new LacunaWebPKI(license)

      return new Promise((resolve, reject) => {
        pki.init({
          ready: () => {
            pkiInstance = pki
            resolve(pki)
          },
          notInstalled: (status, message) => {
            reject({ code: "not_installed", status, message })
          },
          defaultFail: (message) => {
            reject({
              code: "error",
              message: typeof message === "string" ? message : "Falha na extensão de assinatura digital.",
            })
          },
        })
      })
    })
    .catch((error) => {
      // Libera o cache para permitir nova tentativa depois de uma falha.
      pkiReadyPromise = null
      throw error
    })

  return pkiReadyPromise
}

/** Redireciona o usuário para a página de instalação da extensão Web PKI. */
export const redirectToInstallPage = () => {
  if (pkiInstance?.redirectToInstallPage) {
    pkiInstance.redirectToInstallPage()
  }
}

/**
 * Lista os certificados instalados na máquina. Quando `requiredCpf` é
 * informado, retorna apenas os certificados cujo CPF (`pkiBrazil.cpf`)
 * corresponde ao CPF exigido para o signatário; sem CPF, devolve todos e a
 * validação fica a cargo do `complete` (que confere o CPF de forma autoritativa).
 */
export const listSignerCertificates = async (pki, requiredCpf) => {
  const certs = await toPromise(pki.listCertificates())
  const want = onlyDigits(requiredCpf)
  if (!want) {
    return certs
  }
  return certs.filter((cert) => {
    const certCpf = onlyDigits(cert?.pkiBrazil?.cpf)
    return certCpf !== "" && certCpf === want
  })
}

/**
 * Sequência de assinatura:
 *  (a) fail-fast do CPF no browser (bom UX; o certificado nunca é enviado),
 *  (b) `start` na nossa API para obter o token do Rest PKI,
 *  (c) o Web PKI assina localmente contra o Rest PKI usando o token,
 *  (d) `complete` na nossa API finaliza o PAdES e avança o fluxo.
 * O mesmo token percorre os três passos. Retorna o `signerName` do complete.
 */
export const signWithCertificate = async ({ pki, accessCode, thumbprint, requiredCpf, certs }) => {
  const want = onlyDigits(requiredCpf)
  if (want && Array.isArray(certs)) {
    const chosen = certs.find((cert) => cert.thumbprint === thumbprint)
    const certCpf = onlyDigits(chosen?.pkiBrazil?.cpf)
    if (certCpf && certCpf !== want) {
      const err = new Error("Este certificado não pertence ao CPF exigido para este signatário.")
      err.code = "cpf_mismatch"
      throw err
    }
  }

  const startResp = await Api("signer/certificateStart")(accessCode)
  const token = startResp?.data?.data?.token
  if (!token) {
    throw new Error("Não foi possível iniciar a assinatura digital. Tente novamente.")
  }

  await toPromise(pki.signWithRestPki({ thumbprint, token }))

  const completeResp = await Api("signer/certificateComplete")(accessCode, token)
  return completeResp?.data?.data?.signerName
}
